'use client';

import { useSession } from '@shared/auth';
import { getSiteConfig } from '@shared/config';
import { resolveLegacyRegistrationRedirect, ROUTES, setCookie, useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { ErrorStep } from '../GenericSteps/ErrorStep';
import { SuccessStep } from '../GenericSteps/SuccessStep';
import { type FormData, getFormConfig } from './formConfig';
import { getPreservedRegistrationReferrer, isInternalAuthReturnParam, REGISTRATION_REDIRECT_URL_PARAM } from './hooks/registrationAuthResume';
import { useLandingPodData } from './hooks/useLandingPodData';
import { getRegistrationContext, setRegistrationContext } from './registrationContext';
import { getFormSchema } from './RegistrationSchema';
import { AddressStep } from './steps/AddressStep';
import { ChannelPreferencesStep } from './steps/ChannelPreferencesStep';
import { CreateAccountStep } from './steps/CreateAccountStep';
import { DepositLimitsStep } from './steps/DepositLimitsStep';
import { LoadingSuccessStep } from './steps/LoadingSuccessStep';
import { MobileNumberStep } from './steps/MobileNumberStep';
import { PersonalDetailsStep } from './steps/PersonalDetails/PersonalDetailsStep';
import { ProductPreferencesStep } from './steps/ProductPreferencesStep';
import { RegistrationFlowErrorStep } from './steps/RegistrationFlowErrorStep';
import { resetRegistrationFailureKind } from './utils/registrationFailureKind';
import { resolveRegistrationSuccessTracking } from './utils/registrationSuccessTracking';

const LEGACY_REFERRER_HOSTNAME = 'www.lottoland.co.uk';

function useStepsConfig() {
  const t = useTranslations('form');
  const tRegister = useTranslations('register.screens.success');
  const config = useClientConfig();
  const formSchema = getFormSchema(t);
  const form = useForm<FormData>(getFormConfig(t));
  const registrationMarket = getSiteConfig().REGISTRATION;
  const searchParams = useSearchParams();
  const landingPageId = searchParams.get('lpid');
  const { session } = useSession();
  const sessionFirstName = session?.profile?.firstName;

  useEffect(() => {
    resetRegistrationFailureKind();
  }, []);

  // Fetch landing pod data if lpid exists
  const { data: landingPodData } = useLandingPodData({
    landingPageId,
    enabled: !!landingPageId,
  });

  // Capture original URL parameters on first render and store in module-level context
  useEffect(() => {
    if (getRegistrationContext()) {
      return;
    }

    const obcode = searchParams.get('code') || searchParams.get('OBCODE') || null;
    // After the Keycloak round-trip document.referrer is Keycloak, so prefer the original
    // referrer carried in the URL; fall back to document.referrer on the initial visit.
    const documentReferrer = typeof window !== 'undefined' && typeof document !== 'undefined' ? document.referrer : '';
    const referrer = getPreservedRegistrationReferrer(searchParams) ?? documentReferrer;
    const registrationRedirectUrl = searchParams.get(REGISTRATION_REDIRECT_URL_PARAM);
    const allParamsObj = Object.fromEntries([...searchParams.entries()].filter(([key]) => !isInternalAuthReturnParam(key)));

    delete allParamsObj[REGISTRATION_REDIRECT_URL_PARAM];

    setRegistrationContext({
      obcode,
      referrer,
      registrationRedirectUrl,
      allParams: allParamsObj,
    });

    if (registrationRedirectUrl && typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete(REGISTRATION_REDIRECT_URL_PARAM);
        window.history.replaceState({}, '', url.pathname + url.search);
      } catch {
        // registrationRedirectUrl is already stored in context; skip URL cleanup if location is unavailable
      }
    }

    if (obcode) {
      setCookie('ob', obcode, {
        maxAge: 30 * 24 * 60 * 60 * 1000,
        path: '/',
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax',
      });
    }
  }, [searchParams]);

  // Helper functions for step components
  const createSuccessTitle = useCallback(
    (firstName: string) =>
      tRegister.rich('titleWithName', {
        name: firstName,
        br: () => <br />,
        mask: chunks => <span data-track-privacy="Mask">{chunks}</span>,
      }),
    [tRegister]
  );

  const handleErrorAction = useCallback(() => {
    if (typeof window !== 'undefined' && window.open) {
      window.open(ROUTES.HELP_CENTRE.BASE_URL, '_blank');
    }
  }, []);

  const successTrackingAttributes = resolveRegistrationSuccessTracking(form.getValues(), registrationMarket.tracking.successAttributes, searchParams);

  const isReferrerLegacy = useCallback((): boolean => {
    const referrer = getRegistrationContext()?.referrer.trim() ?? '';
    if (!referrer) {
      return false;
    }

    try {
      const url = new URL(referrer);
      return url.hostname.toLowerCase() === LEGACY_REFERRER_HOSTNAME;
    } catch {
      return false;
    }
  }, []);

  // Calculate redirect URL based on landing pod data
  const calculateRedirectUrl = useCallback(() => {
    const defaultRedirectUrl = registrationMarket.defaultRegistrationSuccessPath ?? config.legacySiteUrl ?? ROUTES.FALLBACK_URL;

    // If no lpid, use original logic (redirect to /ob or redirectUrl)
    if (!landingPageId) {
      const code = searchParams.get('code');
      if (code && registrationMarket.usesObRedirectForAcquisitionCode) {
        return isReferrerLegacy() ? `${config.legacySiteUrl}/ob` : '/ob';
      }

      const registrationContext = getRegistrationContext();
      if (registrationMarket.legacyRegistrationRedirect) {
        const legacyResolution = resolveLegacyRegistrationRedirect({
          explicitRedirectUrl: registrationContext?.registrationRedirectUrl,
          config: registrationMarket.legacyRegistrationRedirect,
        });

        if (legacyResolution) {
          return legacyResolution.destination;
        }
      }

      return defaultRedirectUrl;
    }

    // If lpid exists but no landing pod data, fallback to default
    if (!landingPodData?.pods || landingPodData.pods.length === 0) {
      return defaultRedirectUrl;
    }

    // If only one pod item, redirect to that pod's link
    if (landingPodData.pods.length === 1) {
      return landingPodData.pods[0]?.link ?? defaultRedirectUrl;
    }

    // If multiple pod items, redirect to offer-choice page
    return `/offer-choice/${landingPageId}`;
  }, [landingPageId, landingPodData, config.legacySiteUrl, searchParams, isReferrerLegacy, registrationMarket]);

  // Memoized loading step component - passes form to handle registration
  const memoizedLoadingSuccessStep = useMemo(() => <LoadingSuccessStep form={form as unknown as UseFormReturn<Record<string, unknown>>} />, [form]);

  const tScreens = useTranslations('register.screens');
  const errorStepComponent = registrationMarket.usesClassifiedRegistrationErrorStep ? (
    <RegistrationFlowErrorStep />
  ) : (
    <ErrorStep
      fallbackStep="product-preferences"
      errorMessage={tScreens('error.description')}
      actionText={tScreens('error.goBack')}
      onAction={handleErrorAction}
      trackContainer="Failure Confirmation"
      descriptionTrackName="Description"
      actionButtonTrackName="Back"
    />
  );

  const steps = [
    {
      id: 'create-account',
      component: <CreateAccountStep />,
      schema: formSchema.shape.createAccount,
      dataTrackContainer: 'Create Account',
      dataTrackStep: '1',
    },
    {
      id: 'personal-details',
      component: <PersonalDetailsStep />,
      schema: formSchema.shape.personalDetails,
      dataTrackContainer: 'Personal Details',
      dataTrackStep: '2',
      navTitle: tScreens('personalDetails.navTitle') || undefined,
    },
    {
      id: 'address',
      component: <AddressStep />,
      schema: formSchema.shape.address,
      dataTrackContainer: 'Address',
      dataTrackStep: '3',
      navTitle: tScreens('address.navTitle') || undefined,
    },
    {
      id: 'mobile-number',
      component: <MobileNumberStep />,
      schema: formSchema.shape.mobileNumber,
      dataTrackContainer: 'Mobile',
      dataTrackStep: '4',
      navTitle: tScreens('mobileNumber.navTitle') || undefined,
    },
    // TODO: not part of Wave 0, because the Core Domain API is not yet implemented
    // {
    //   id: 'otp-validation',
    //   component: <OtpValidationStep />,
    //   schema: formSchema.shape.otpValidation,
    // },
    {
      id: 'product-preferences',
      component: <ProductPreferencesStep />,
      schema: formSchema.shape.productPreferences,
      dataTrackContainer: 'Products',
      dataTrackStep: '5',
      condition: () => registrationMarket.showsGamblingMarketingPreferenceSteps,
    },
    {
      id: 'channel-preferences',
      component: <ChannelPreferencesStep />,
      schema: formSchema.shape.channelPreferences,
      dataTrackContainer: 'Communication Options',
      dataTrackStep: '6',
      // Only show this step if the user has selected gambling preferences
      condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
        return registrationMarket.showsGamblingMarketingPreferenceSteps && status === 'has-preferences';
      },
    },
    {
      id: 'deposit-limits',
      component: <DepositLimitsStep />,
      schema: formSchema.shape.depositLimits,
      dataTrackContainer: 'Set Deposit Limit',
      dataTrackStep: registrationMarket.tracking.depositLimitsStep,
      navTitle: tScreens('depositLimits.navTitle') || undefined,
    },
    {
      id: 'processing',
      component: memoizedLoadingSuccessStep,
      schema: z.any(),
      navTitle: tScreens('createAccount.title'),
      // Show this step when status is set to 'success' by DepositLimitsStep
      condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
        return status === 'success';
      },
    },
    {
      id: 'completed',
      component: (
        <SuccessStep
          title={createSuccessTitle}
          description={
            <>
              <p>{tRegister('accountCreated')}</p>
            </>
          }
          redirectUrl={calculateRedirectUrl()}
          firstName={sessionFirstName}
          extractFirstNameFromForm={true}
          trackContainer="Success Confirmation"
          trackName="Deposit"
          additionalTrackingProps={successTrackingAttributes}
        />
      ),
      schema: z.any(),
      navTitle: tScreens('createAccount.title'),
      // Show this step when status is set to 'completed' by LoadingSuccessStep
      condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
        return status === 'completed';
      },
    },
    {
      id: 'deposit-limits-success',
      component: (
        <SuccessStep
          title={createSuccessTitle}
          description={
            <>
              <p>{tRegister('accountCreated')}</p>
            </>
          }
          firstName={sessionFirstName}
          extractFirstNameFromForm={true}
          trackContainer="Success Confirmation"
          trackName="Deposit Limits"
          additionalTrackingProps={successTrackingAttributes}
        />
      ),
      schema: z.any(),
      navTitle: tScreens('createAccount.title'),
      // Show this step when status is set to 'deposit-limits' by LoadingSuccessStep
      condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
        return status === 'deposit-limits';
      },
    },
    {
      id: 'error',
      component: errorStepComponent,
      schema: z.any(),
      showNavigation: false,
      // Only show this step if registration failed
      condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
        return status === 'error';
      },
    },
  ];

  return { steps, form };
}

export { getRegistrationContext, REGISTRATION_REDIRECT_URL_PARAM, setRegistrationContext, useStepsConfig };
